export const NATIVE_LOOP_OFFLINE_CONTRACT_VERSION = 1;
export const NATIVE_LOOP_OFFLINE_BRIDGE_VERSION = 2;
export const NATIVE_LOOP_MEDIA_BRIDGE_VERSION = 3;
export const NATIVE_LOOP_MANIFEST_MAX_AGE_MS = 72 * 60 * 60 * 1000;
export const NATIVE_LOOP_TELEMETRY_TTL_MS = 72 * 60 * 60 * 1000;
export const NATIVE_LOOP_MEDIA_MAX_HANDOFF_BYTES = 50 * 1024 * 1024;
const NATIVE_TELEMETRY_REPLAY_BATCH_SIZE = 4;

const getWindow = () => {
  try {
    return typeof window !== 'undefined' ? window : null;
  } catch (_) {
    return null;
  }
};

const normalizeString = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed && trimmed !== 'null' && trimmed !== 'undefined' ? trimmed : null;
};

export const resolveNativeLoopFingerprint = (fingerprint = null) => {
  const win = getWindow();
  const explicit = normalizeString(fingerprint);
  if (explicit) return explicit;

  const globalFingerprint = normalizeString(win?.myFingerPrint)
    || normalizeString(win?.__TRILLBOARD_SCREEN_FINGERPRINT__);
  if (globalFingerprint) return globalFingerprint;

  try {
    return normalizeString(win?.localStorage?.getItem('trillboard_screen_identity_fingerprint'));
  } catch (_) {
    return null;
  }
};

export const getNativeLoopOfflineBridge = () => {
  const bridge = getWindow()?.TrillboardsNativeDevice;
  if (!bridge || typeof bridge !== 'object') {
    return null;
  }

  if (typeof bridge.getBridgeVersion === 'function') {
    const version = Number(bridge.getBridgeVersion());
    if (Number.isFinite(version) && version < NATIVE_LOOP_OFFLINE_BRIDGE_VERSION) {
      return null;
    }
  }

  return bridge;
};

export const hasNativeLoopOfflineBridge = () => {
  const bridge = getNativeLoopOfflineBridge();
  return Boolean(
    bridge
    && typeof bridge.putLoopManifestSnapshot === 'function'
    && typeof bridge.putAssetInventory === 'function'
    && typeof bridge.putPlaybackState === 'function'
    && typeof bridge.enqueueTelemetry === 'function'
  );
};

export const hasNativeLoopMediaBridge = () => {
  const bridge = getNativeLoopOfflineBridge();
  if (!bridge || typeof bridge !== 'object') return false;

  if (typeof bridge.getBridgeVersion === 'function') {
    const version = Number(bridge.getBridgeVersion());
    if (Number.isFinite(version) && version < NATIVE_LOOP_MEDIA_BRIDGE_VERSION) {
      return false;
    }
  }

  return Boolean(
    typeof bridge.putMediaAsset === 'function'
    && typeof bridge.getMediaAsset === 'function'
    && typeof bridge.getMediaAssetMap === 'function'
  );
};

const safeNativeCall = (methodName, ...args) => {
  try {
    const bridge = getNativeLoopOfflineBridge();
    if (!bridge || typeof bridge[methodName] !== 'function') {
      return null;
    }
    return bridge[methodName](...args);
  } catch (_) {
    return null;
  }
};

const safeJsonStringify = (value) => {
  try {
    return JSON.stringify(value);
  } catch (_) {
    return null;
  }
};

const safeJsonParse = (value) => {
  try {
    if (!value) return null;
    if (typeof value === 'object') return value;
    return JSON.parse(String(value));
  } catch (_) {
    return null;
  }
};

const stableHash = (value) => {
  const raw = typeof value === 'string' ? value : safeJsonStringify(value);
  if (!raw) return null;
  let hash = 5381;
  for (let index = 0; index < raw.length; index += 1) {
    hash = ((hash << 5) + hash) ^ raw.charCodeAt(index);
  }
  return `js-${(hash >>> 0).toString(16).padStart(8, '0')}`;
};

const publishNativeStatus = (screenId, details = {}) => {
  const win = getWindow();
  if (!win) return;
  try {
    win.__TRILLBOARDS_NATIVE_LOOP_OFFLINE__ = {
      ...(win.__TRILLBOARDS_NATIVE_LOOP_OFFLINE__ || {}),
      screen_id: String(screenId || ''),
      updated_at_ms: Date.now(),
      ...details,
    };
  } catch (_) {}
};

export const readNativeLoopOfflineStatus = (screenId) => {
  if (!screenId) return null;
  const raw = safeNativeCall('getOfflineCacheStatus', String(screenId));
  const parsed = safeJsonParse(raw);
  if (!parsed?.ok) {
    publishNativeStatus(screenId, {
      available: hasNativeLoopOfflineBridge(),
      last_error_reason: parsed?.reason || 'status_unavailable',
    });
    return null;
  }

  const status = {
    native_bridge_available: true,
    using_native_manifest: parsed.using_native_manifest === true,
    native_manifest_age_ms: parsed.native_manifest_age_ms ?? null,
    native_cached_asset_count: Number(parsed.native_cached_asset_count) || 0,
    native_required_asset_count: Number(parsed.native_required_asset_count) || 0,
    native_queued_event_count: Number(parsed.native_queued_event_count) || 0,
    native_last_flush_at_ms: parsed.last_flush_at_ms ?? null,
    ...(parsed.native_media_asset_count !== undefined ? {
      native_media_asset_count: Number(parsed.native_media_asset_count) || 0,
    } : {}),
    ...(parsed.native_media_bytes !== undefined ? {
      native_media_bytes: Number(parsed.native_media_bytes) || 0,
    } : {}),
    ...(parsed.native_media_missing_count !== undefined ? {
      native_media_missing_count: Number(parsed.native_media_missing_count) || 0,
    } : {}),
  };
  publishNativeStatus(screenId, {
    available: true,
    cache_status: status,
  });
  return status;
};

export const putNativeLoopManifestSnapshot = (screenId, fingerprint, record) => {
  const nativeFingerprint = resolveNativeLoopFingerprint(fingerprint);
  if (!screenId || !nativeFingerprint || !record?.envelope?.data) {
    return false;
  }

  const policy = record.envelope.data.playback_policy || {};
  const loopPlan = policy.loop_plan || {};
  const segments = Array.isArray(loopPlan.segments) ? loopPlan.segments : [];
  const savedAtMs = Number(record.saved_at_ms) || Date.now();
  const envelopeJson = safeJsonStringify(record.envelope);
  if (!envelopeJson) return false;

  const payload = {
    version: NATIVE_LOOP_OFFLINE_CONTRACT_VERSION,
    screen_id: String(screenId),
    fingerprint: nativeFingerprint,
    saved_at_ms: savedAtMs,
    expires_at_ms: savedAtMs + NATIVE_LOOP_MANIFEST_MAX_AGE_MS,
    endpoint: record.endpoint || null,
    etag: record.etag || null,
    policy_version: policy.policy_version || policy.version || 'v2',
    delivery_mode: policy.delivery_mode || null,
    plan_id: loopPlan.plan_id ?? loopPlan.id ?? null,
    loop_duration_ms: loopPlan.loop_duration_ms ?? loopPlan.duration_ms ?? null,
    segment_count: segments.length,
    manifest_hash: stableHash(envelopeJson),
    cached_asset_count: Number(record.cached_asset_count) || 0,
    envelope_json: envelopeJson,
  };

  const body = safeJsonStringify(payload);
  const saved = body ? safeNativeCall('putLoopManifestSnapshot', body) === true : false;
  publishNativeStatus(screenId, {
    manifest_mirror_saved: saved,
    manifest_mirror_saved_at_ms: Date.now(),
  });
  return saved;
};

const arrayBufferToBase64 = (buffer) => {
  try {
    if (typeof Buffer !== 'undefined') {
      return Buffer.from(buffer).toString('base64');
    }
  } catch (_) {}

  try {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    for (let index = 0; index < bytes.length; index += chunkSize) {
      const chunk = bytes.subarray(index, index + chunkSize);
      binary += String.fromCharCode.apply(null, chunk);
    }
    return typeof btoa === 'function' ? btoa(binary) : null;
  } catch (_) {
    return null;
  }
};

const blobToBase64 = async (blob) => {
  if (!blob || typeof blob !== 'object') return null;

  if (typeof blob.arrayBuffer === 'function') {
    const buffer = await blob.arrayBuffer();
    return arrayBufferToBase64(buffer);
  }

  if (typeof FileReader === 'function') {
    return new Promise((resolve) => {
      try {
        const reader = new FileReader();
        reader.onload = () => {
          const value = String(reader.result || '');
          resolve(value.includes('base64,') ? value.substring(value.indexOf('base64,') + 7) : null);
        };
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      } catch (_) {
        resolve(null);
      }
    });
  }

  return null;
};

export const putNativeMediaAsset = async (screenId, fingerprint, asset, mediaRecord, options = {}) => {
  const nativeFingerprint = resolveNativeLoopFingerprint(fingerprint);
  if (!screenId || !nativeFingerprint || !asset?.url || !mediaRecord?.blob || !hasNativeLoopMediaBridge()) {
    return { saved: false, reason: 'native_media_bridge_unavailable' };
  }

  const byteCount = Number(mediaRecord.size ?? mediaRecord.blob?.size) || 0;
  const maxBytes = Number(options.maxBytes) || NATIVE_LOOP_MEDIA_MAX_HANDOFF_BYTES;
  if (byteCount <= 0) {
    return { saved: false, reason: 'empty_media_blob' };
  }
  if (byteCount > maxBytes) {
    return { saved: false, reason: 'media_blob_too_large', byteCount, maxBytes };
  }

  const contentBase64 = await blobToBase64(mediaRecord.blob);
  if (!contentBase64) {
    return { saved: false, reason: 'media_base64_unavailable' };
  }

  const storedAtMs = Number(mediaRecord.timestamp) || Date.now();
  const ttlMs = Number(asset.ttl_ms) || NATIVE_LOOP_MANIFEST_MAX_AGE_MS;
  const body = safeJsonStringify({
    version: NATIVE_LOOP_OFFLINE_CONTRACT_VERSION,
    screen_id: String(screenId),
    fingerprint: nativeFingerprint,
    source_url: asset.url,
    content_type: mediaRecord.type || asset.content_type || null,
    byte_count: byteCount,
    stored_at_ms: storedAtMs,
    expires_at_ms: storedAtMs + ttlMs,
    priority: Number(asset.priority) || 3,
    role: asset.role || null,
    content_base64: contentBase64,
  });

  const parsed = safeJsonParse(body ? safeNativeCall('putMediaAsset', body) : null);
  const saved = parsed?.ok === true;
  publishNativeStatus(screenId, {
    media_asset_mirror_saved: saved,
    media_asset_mirror_saved_at_ms: Date.now(),
    last_media_asset_reason: saved ? null : (parsed?.reason || 'media_put_failed'),
  });
  return saved
    ? { saved: true, asset: parsed }
    : { saved: false, reason: parsed?.reason || 'media_put_failed' };
};

export const readNativeMediaAssetMap = (screenId, fingerprint) => {
  const nativeFingerprint = resolveNativeLoopFingerprint(fingerprint);
  if (!screenId || !nativeFingerprint || !hasNativeLoopMediaBridge()) {
    return null;
  }

  const parsed = safeJsonParse(safeNativeCall('getMediaAssetMap', String(screenId), nativeFingerprint));
  if (!parsed?.ok) {
    publishNativeStatus(screenId, {
      media_asset_map_available: false,
      last_media_asset_reason: parsed?.reason || 'media_map_unavailable',
    });
    return null;
  }

  publishNativeStatus(screenId, {
    media_asset_map_available: true,
    native_media_asset_count: Number(parsed.native_media_asset_count) || 0,
    native_media_bytes: Number(parsed.native_media_bytes) || 0,
  });
  return parsed;
};

export const putNativeAssetInventory = (screenId, fingerprint, inventory) => {
  const nativeFingerprint = resolveNativeLoopFingerprint(fingerprint);
  if (!screenId || !nativeFingerprint || !inventory) {
    return false;
  }

  const payload = {
    version: NATIVE_LOOP_OFFLINE_CONTRACT_VERSION,
    screen_id: String(screenId),
    fingerprint: nativeFingerprint,
    updated_at_ms: Number(inventory.updated_at_ms) || Date.now(),
    required_asset_count: Number(inventory.required_asset_count) || 0,
    cached_asset_count: Number(inventory.cached_asset_count) || 0,
    missing_asset_count: Number(inventory.missing_asset_count) || 0,
    failed_asset_count: Number(inventory.failed_asset_count) || 0,
    assets: Array.isArray(inventory.assets) ? inventory.assets : [],
  };

  const body = safeJsonStringify(payload);
  const saved = body ? safeNativeCall('putAssetInventory', body) === true : false;
  publishNativeStatus(screenId, {
    asset_inventory_mirror_saved: saved,
    asset_inventory_mirror_saved_at_ms: Date.now(),
  });
  return saved;
};

export const putNativePlaybackState = (screenId, fingerprint, loopPlaybackState, renderedSource, options = {}) => {
  const nativeFingerprint = resolveNativeLoopFingerprint(fingerprint);
  if (!screenId || !nativeFingerprint || !loopPlaybackState) {
    return false;
  }

  const transitionTimestampMs = Number(options.transitionTimestampMs) || Date.now();
  const payload = {
    version: NATIVE_LOOP_OFFLINE_CONTRACT_VERSION,
    screen_id: String(screenId),
    fingerprint: nativeFingerprint,
    session_id: options.sessionId || loopPlaybackState.session_id || null,
    plan_id: loopPlaybackState.plan_id ?? null,
    cycle_number: Number(loopPlaybackState.cycle_number) || 0,
    slot_index: Number.isFinite(Number(loopPlaybackState.slot_index))
      ? Number(loopPlaybackState.slot_index)
      : -1,
    tier: loopPlaybackState.tier || null,
    reservation_id: loopPlaybackState.reservation_id || null,
    selected_creative_id: loopPlaybackState.selected_creative_id || null,
    rendered_source: renderedSource?.source || renderedSource?.type || null,
    transition_timestamp_ms: transitionTimestampMs,
    media_url_hash: renderedSource?.url ? stableHash(renderedSource.url) : null,
  };

  const body = safeJsonStringify(payload);
  const saved = body ? safeNativeCall('putPlaybackState', body) === true : false;
  publishNativeStatus(screenId, {
    playback_state_mirror_saved: saved,
    playback_state_mirror_saved_at_ms: Date.now(),
  });
  return saved;
};

export const enqueueNativeTelemetry = (screenId, fingerprint, url, payload, eventKey, options = {}) => {
  const nativeFingerprint = resolveNativeLoopFingerprint(fingerprint);
  if (!screenId || !nativeFingerprint || !url || !payload || !eventKey) {
    return false;
  }

  const queuedAtMs = Number(options.queuedAtMs) || Date.now();
  const body = safeJsonStringify({
    version: NATIVE_LOOP_OFFLINE_CONTRACT_VERSION,
    screen_id: String(screenId),
    fingerprint: nativeFingerprint,
    event_key: eventKey,
    url,
    queued_at_ms: queuedAtMs,
    expires_at_ms: queuedAtMs + NATIVE_LOOP_TELEMETRY_TTL_MS,
    attempt_count: Number(options.attemptCount) || 0,
    payload,
  });

  const saved = body ? safeNativeCall('enqueueTelemetry', body) === true : false;
  publishNativeStatus(screenId, {
    telemetry_mirror_saved: saved,
    telemetry_mirror_saved_at_ms: Date.now(),
  });
  return saved;
};

export const markNativeTelemetryFlushed = (screenId, eventKeys = []) => {
  const keys = Array.isArray(eventKeys)
    ? eventKeys.map(normalizeString).filter(Boolean)
    : [];
  if (!screenId || keys.length === 0) {
    return false;
  }

  const body = safeJsonStringify(keys);
  const saved = body ? safeNativeCall('markTelemetryFlushed', String(screenId), body) === true : false;
  publishNativeStatus(screenId, {
    telemetry_flush_mirror_saved: saved,
    telemetry_flush_mirror_saved_at_ms: Date.now(),
  });
  return saved;
};

const isNativeFlushOnline = () => {
  try {
    return typeof navigator === 'undefined' || navigator.onLine !== false;
  } catch (_) {
    return true;
  }
};

const readNativeTelemetryQueue = (screenId, maxItems) => {
  if (!screenId) return null;
  const bridge = getNativeLoopOfflineBridge();
  if (!bridge || typeof bridge.peekTelemetryQueue !== 'function') {
    return null;
  }
  const parsed = safeJsonParse(safeNativeCall('peekTelemetryQueue', String(screenId), maxItems));
  return parsed?.ok === true ? parsed : null;
};

const parseNativeTelemetryPayload = (item) => {
  if (!item || typeof item !== 'object') return null;
  if (item.payload && typeof item.payload === 'object') return item.payload;
  return safeJsonParse(item.payload_json);
};

const getNativeFlushBeacon = (options = {}) => {
  if (options.preferBeacon === false) return null;
  if (typeof options.beaconImpl === 'function') return options.beaconImpl;

  try {
    const nav = typeof navigator !== 'undefined' ? navigator : null;
    if (nav && typeof nav.sendBeacon === 'function') {
      return nav.sendBeacon.bind(nav);
    }
  } catch (_) {}

  return null;
};

const hasNativeFlushTransport = (options = {}) => {
  const bridge = getNativeLoopOfflineBridge();
  if (bridge && typeof bridge.flushTelemetryQueue === 'function') return true;
  const fetchImpl = options.fetchImpl || (typeof fetch === 'function' ? fetch : null);
  const beaconImpl = getNativeFlushBeacon(options);
  return Boolean(fetchImpl || (options.preferBeacon === true && beaconImpl && typeof Blob === 'function'));
};

const flushNativeTelemetryQueueViaBridge = (screenId, maxBatch, options = {}) => {
  if (options.preferNativeHttp === false) return null;
  const bridge = getNativeLoopOfflineBridge();
  if (!bridge || typeof bridge.flushTelemetryQueue !== 'function') return null;

  const parsed = safeJsonParse(safeNativeCall('flushTelemetryQueue', String(screenId), maxBatch));
  if (!parsed || parsed.ok !== true) {
    return {
      flushed: 0,
      remaining: Number(parsed?.remaining ?? parsed?.queued_event_count ?? 0) || 0,
      skipped: false,
      nativeHttp: true,
      error: parsed?.reason || 'native_flush_failed',
    };
  }

  return {
    flushed: Number(parsed.flushed || 0),
    remaining: Number(parsed.remaining ?? parsed.queued_event_count ?? 0) || 0,
    skipped: false,
    nativeHttp: true,
    flushedEventKeys: Array.isArray(parsed.event_keys) ? parsed.event_keys.map(String).filter(Boolean) : [],
  };
};

const postNativeTelemetryPayload = async (url, payload, options = {}) => {
  const body = safeJsonStringify(payload);
  if (!url || !body) return false;

  const fetchImpl = options.fetchImpl || (typeof fetch === 'function' ? fetch : null);
  const postWithFetch = async () => {
    if (!fetchImpl) return false;
    try {
      const response = await fetchImpl(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      });
      return response?.ok === true || response?.type === 'opaque';
    } catch (_) {}
    return false;
  };

  if (options.preferBeacon !== true) {
    return postWithFetch();
  }

  const beaconImpl = getNativeFlushBeacon(options);
  if (beaconImpl && typeof Blob === 'function') {
    try {
      const blob = new Blob([body], { type: 'application/json' });
      if (beaconImpl(url, blob)) return true;
    } catch (_) {}
  }

  return postWithFetch();
};

export const flushNativeTelemetryQueue = async (screenId, options = {}) => {
  const maxBatch = Math.max(1, Number(options.maxBatch) || NATIVE_TELEMETRY_REPLAY_BATCH_SIZE);
  const maxBatches = Math.max(1, Number(options.maxBatches) || 24);

  if (!screenId || !hasNativeFlushTransport(options) || !isNativeFlushOnline()) {
    return { flushed: 0, remaining: 0, skipped: true };
  }

  const nativeHttpResult = flushNativeTelemetryQueueViaBridge(screenId, maxBatch, options);
  if (nativeHttpResult) return nativeHttpResult;

  let flushed = 0;
  let remaining = 0;

  for (let batchIndex = 0; batchIndex < maxBatches; batchIndex += 1) {
    const queue = readNativeTelemetryQueue(screenId, maxBatch);
    if (!queue) {
      return { flushed, remaining, skipped: true };
    }

    const items = Array.isArray(queue.items) ? queue.items : [];
    remaining = Math.max(0, Number(queue.queued_event_count) || items.length || 0);
    if (remaining <= 0 || items.length === 0) {
      return { flushed, remaining: 0, skipped: false };
    }

    const flushedKeys = [];
    for (const item of items) {
      const eventKey = normalizeString(item.event_key);
      const url = normalizeString(item.url);
      const payload = parseNativeTelemetryPayload(item);
      if (!eventKey || !url || !payload) continue;

      const delivered = await postNativeTelemetryPayload(url, payload, options);
      if (delivered) {
        flushedKeys.push(eventKey);
      }
    }

    if (flushedKeys.length === 0) {
      return { flushed, remaining, skipped: false };
    }

    if (!markNativeTelemetryFlushed(screenId, flushedKeys)) {
      return { flushed, remaining, skipped: false, markFailed: true };
    }

    flushed += flushedKeys.length;
    remaining = Math.max(0, remaining - flushedKeys.length);
    if (remaining <= 0 || items.length < maxBatch) {
      return { flushed, remaining, skipped: false };
    }
  }

  return { flushed, remaining, skipped: false };
};
